import React from 'react';
import { useAdminMode } from '../context/AdminModeContext';

/**
 * Floating page header that pairs visually with the bottom pill nav.
 *
 *   • Transparent canvas — the page background shows through.
 *   • Optional title on the left (omit `title` for an actions-only bar).
 *     When shown, uses the same brown as the nav's active state.
 *   • Children (usually small dark icon circles) float on the right
 *     without a wrapper, echoing the nav's active-pill shape.
 *
 * Pages still wrap this in `fixed top-0 left-0 right-0 z-50`; we
 * intentionally keep it a flow element so callers control stacking.
 */
export default function Header({ title, children }) {
  const { adminMode } = useAdminMode();
  const label = adminMode ? 'Admin mode' : title;
  const showTitle = label != null && String(label).trim() !== '';

  return (
    <div
      className={`w-full flex items-center ${showTitle ? 'justify-between' : 'justify-end'}`}
      style={{
        paddingLeft: 24,
        paddingRight: 24,
        // Tighter bar than before; top respects notch / home indicator.
        paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
        paddingBottom: 12,
        pointerEvents: 'none',
        background: 'transparent',
      }}
    >
      {showTitle && (
        <div
          style={{
            color: '#44231E',
            fontSize: 20,
            fontFamily: 'Poppins',
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            pointerEvents: 'auto',
          }}
          className="flex min-h-[44px] max-w-[58%] items-center truncate"
        >
          {label}
        </div>
      )}

      <div
        className="flex min-h-[44px] items-center gap-2"
        style={{ pointerEvents: 'auto' }}
      >
        {children}
      </div>
    </div>
  );
}
